import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import {
  getShopImages,
  uploadShopImage,
  deleteShopImage,
} from '../services/shopImages';

const ShopImagesDialog = ({ open, onClose, shopId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [imageType, setImageType] = useState('banner');
  const [selectedTab, setSelectedTab] = useState('all');

  useEffect(() => {
    if (open && shopId) {
      fetchImages();
    }
  }, [open, shopId]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const data = await getShopImages(shopId);
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      setUploadProgress(0);
      try {
        const response = await uploadShopImage(shopId, file, imageType, (progress) => {
          setUploadProgress(progress);
        });
        
        let newImages = [];
        if (Array.isArray(response)) {
          newImages = response;
        } else if (response && Array.isArray(response.data)) {
          newImages = response.data;
        } else if (response && response.data) {
          newImages = [response.data];
        }

        if (newImages.length > 0) {
          setImages(prev => [...prev, ...newImages]);
          showSnackbar('Image uploaded successfully!');
        } else {
          fetchImages(); // Fallback to refresh
        }

      } catch (error) {
        showSnackbar('Failed to upload image.', 'error');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleDeleteClick = (image) => {
    setSelectedImage(image);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedImage) return;
    setDeleting(selectedImage.id);
    try {
      await deleteShopImage(selectedImage.id);
      setImages(prev => prev.filter(img => img.id !== selectedImage.id));
      showSnackbar('Image deleted successfully!');
    } catch (error) {
      showSnackbar('Failed to delete image.', 'error');
    } finally {
      setDeleting(null);
      setDeleteDialogOpen(false);
      setSelectedImage(null);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Shop Images
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage gallery images for this shop
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Type</InputLabel>
            <Select
              value={imageType}
              label="Type"
              onChange={(e) => setImageType(e.target.value)}
            >
              <MenuItem value="banner">Banner</MenuItem>
              <MenuItem value="logo">Logo</MenuItem>
              <MenuItem value="parking_entrance">Parking Entrance</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            component="label"
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
            disabled={uploading}
            sx={{
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
              }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleUpload}
            />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} aria-label="Image type tabs">
            <Tab label="All" value="all" />
            <Tab label="Banners" value="banner" />
            <Tab label="Logos" value="logo" />
            <Tab label="Parking" value="parking_entrance" />
          </Tabs>
        </Box>
        {uploading && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : images.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 6,
              px: 4,
              background: '#f9fafb',
              borderRadius: 2,
              border: '2px dashed #d1d5db'
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
              No Images Yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Upload images to display in the shop gallery
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {images.filter(image => selectedTab === 'all' || image.type === selectedTab).map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={image.imageUrl}
                      alt={`Shop Image ${image.id}`}
                      style={{
                        width: '100%',
                        height: '180px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(image)}
                      disabled={deleting === image.id}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#dc2626',
                        '&:hover': {
                          backgroundColor: '#dc2626',
                          color: '#fff',
                        },
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}
                    >
                      {deleting === image.id ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Delete fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                  <Box sx={{ p: 1.5, background: '#f9fafb' }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      Added: {image.createdAt ? new Date(image.createdAt).toLocaleDateString() : '-'}
                    </Typography>
                     <Chip label={image.type || 'N/A'} size="small" />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button 
          onClick={onClose}
          sx={{ 
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { background: '#f3f4f6' }
          }}
        >
          Close
        </Button>
      </DialogActions>


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            border: '1px solid rgba(0,0,0,0.08)',
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#dc2626',
          }}
        >
          <Delete />
          Delete Image
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6b7280' }}>
            Are you sure you want to delete this image? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#6b7280',
              '&:hover': { color: '#374151', background: 'rgba(0,0,0,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deleting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
                boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
              },
            }}
          >
            {deleting ? <CircularProgress size={24} color="inherit" /> : 'Delete Image'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ShopImagesDialog;
